import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Check, Loader2, AlertCircle, RotateCcw, Upload, Image as ImageIcon, Sparkles, Film, Wand2, Play } from 'lucide-react';
import { FunnelStep } from '../../types/listing-funnel';
import type { PollPreviewResponse } from '../../types/listing-funnel';
import { FUNNEL_STEPS, ERROR_MESSAGES, POLL_INTERVAL_MS, MAX_POLL_ATTEMPTS, FUNNEL_BASE_PATH } from './constants';
import { pollListingPreview, logListingEvent } from '../../services/listing-funnel';

interface ListingProcessingProps {
  jobId: string;
  shareSlug: string;
  initialImages: string[];
  onComplete: (shareSlug: string) => void;
  onError: () => void;
}

function statusToStep(status: string): FunnelStep {
  switch (status) {
    case 'pending': return FunnelStep.VALIDATING;
    case 'extracting': return FunnelStep.EXTRACTING;
    case 'generating_clips': return FunnelStep.GENERATING;
    case 'rendering': return FunnelStep.RENDERING;
    case 'completed':
    case 'cached': return FunnelStep.RENDERING;
    default: return FunnelStep.VALIDATING;
  }
}

const STEP_ICONS = [ImageIcon, Upload, Sparkles, Wand2, Film];

const STEP_TIPS = [
  'Your images are being prepared for processing...',
  'Uploading to our AI pipeline...',
  'Your project is queued — almost there...',
  'AI is generating smooth cinematic camera motion for each image...',
  'Adding transitions, music, and branding to your video...',
];

const ListingProcessing: React.FC<ListingProcessingProps> = ({
  jobId,
  shareSlug,
  initialImages,
  onComplete,
  onError,
}) => {
  const [currentStep, setCurrentStep] = useState<FunnelStep>(FunnelStep.SELECTING);
  const [progress, setProgress] = useState(15);
  const [images, setImages] = useState<string[]>(initialImages);
  const [failed, setFailed] = useState(false);
  const [failureInfo, setFailureInfo] = useState<{ title: string; description: string } | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const pollCount = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTime = useRef(Date.now());

  // Elapsed time counter
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startTime.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const poll = useCallback(async () => {
    try {
      pollCount.current++;
      const result: PollPreviewResponse = await pollListingPreview(jobId);

      if (result.images && result.images.length > 0) {
        setImages(result.images);
      }

      setProgress(result.progress);
      setCurrentStep(statusToStep(result.status));

      if (result.status === 'completed' || result.status === 'cached') {
        logListingEvent('preview_generation_completed', { jobId });
        onComplete(result.shareSlug ?? shareSlug);
        return;
      }

      if (result.status === 'failed') {
        const errorKey = result.error ?? 'render_failed';
        setFailureInfo(ERROR_MESSAGES[errorKey] ?? ERROR_MESSAGES.generic);
        setFailed(true);
        return;
      }

      if (pollCount.current >= MAX_POLL_ATTEMPTS) {
        setFailureInfo(ERROR_MESSAGES.render_failed);
        setFailed(true);
        return;
      }

      timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    } catch {
      if (pollCount.current >= MAX_POLL_ATTEMPTS) {
        setFailureInfo(ERROR_MESSAGES.generic);
        setFailed(true);
      } else {
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS * 2);
      }
    }
  }, [jobId, shareSlug, onComplete]);

  useEffect(() => {
    setCurrentStep(FunnelStep.GENERATING);
    setProgress(25);
    timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [poll]);

  const getStepStatus = (step: FunnelStep): 'done' | 'active' | 'pending' => {
    if (failed) return step <= currentStep ? 'done' : 'pending';
    if (step < currentStep) return 'done';
    if (step === currentStep) return 'active';
    return 'pending';
  };

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  return (
    <main className="relative z-10">
      <div className="container mx-auto px-4 py-16 md:py-20">
        <div className="max-w-2xl mx-auto">

          {/* Header */}
          <div className="text-center mb-10">
            {!failed ? (
              <>
                <div className="relative w-20 h-20 mx-auto mb-6">
                  <div className="absolute inset-0 rounded-full border-4 border-slate-800" />
                  <div
                    className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-500 border-r-cyan-500 animate-spin"
                    style={{ animationDuration: '2s' }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Play size={24} className="text-blue-400 ml-1" />
                  </div>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  Creating your preview video
                </h1>
                <p className="text-slate-400">
                  This usually takes 1–2 minutes
                </p>
                <p className="text-xs text-slate-600 mt-1">
                  Elapsed: {formatTime(elapsedSeconds)}
                </p>
              </>
            ) : (
              <>
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-900/20 border-2 border-red-500/30 flex items-center justify-center">
                  <AlertCircle size={32} className="text-red-400" />
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  Something went wrong
                </h1>
                <p className="text-slate-400">
                  We couldn't complete your preview video.
                </p>
              </>
            )}
          </div>

          {/* Image strip with animated highlight */}
          {images.length > 0 && !failed && (
            <div className="flex items-center justify-center gap-2 mb-10">
              {images.map((img, i) => {
                const isProcessing = currentStep === FunnelStep.GENERATING;
                return (
                  <div
                    key={i}
                    className={`relative w-20 h-14 md:w-28 md:h-20 rounded-lg overflow-hidden transition-all duration-500 ${
                      isProcessing
                        ? 'border-2 border-blue-500/50 shadow-lg shadow-blue-500/20'
                        : 'border-2 border-slate-700/60'
                    }`}
                  >
                    <img
                      src={img}
                      alt={`Image ${i + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                    {isProcessing && (
                      <div className="absolute inset-0 bg-blue-500/10 animate-pulse" />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Visual pipeline */}
          {!failed && (
            <div className="mb-10 pl-2">
              {FUNNEL_STEPS.map((step, i) => {
                const status = getStepStatus(step.step);
                const StepIcon = STEP_ICONS[i] ?? Sparkles;
                const isActive = status === 'active';
                const isDone = status === 'done';
                const isLast = i === FUNNEL_STEPS.length - 1;

                return (
                  <div key={step.step} className="flex gap-4">
                    {/* Left column: dot + connector line */}
                    <div className="flex flex-col items-center w-10 shrink-0">
                      <div className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 ${
                        isDone
                          ? 'bg-green-600/20 border-2 border-green-500/40'
                          : isActive
                          ? 'bg-blue-600/20 border-2 border-blue-500/50 shadow-lg shadow-blue-500/20'
                          : 'bg-slate-800/80 border-2 border-slate-700/50'
                      }`}>
                        {isDone ? (
                          <Check size={18} className="text-green-400" />
                        ) : isActive ? (
                          <StepIcon size={18} className="text-blue-400 animate-pulse" />
                        ) : (
                          <StepIcon size={16} className="text-slate-600" />
                        )}
                      </div>
                      {!isLast && (
                        <div className={`w-0.5 flex-1 my-1 transition-colors duration-500 ${
                          isDone ? 'bg-green-500/40' : 'bg-slate-800'
                        }`} />
                      )}
                    </div>

                    {/* Right column: content */}
                    <div className={`flex-1 pb-6 pt-1.5 ${!isActive && !isDone ? 'opacity-35' : ''}`}>
                      <div className={`rounded-xl p-4 transition-all duration-500 ${
                        isActive
                          ? 'bg-blue-950/40 border border-blue-500/30'
                          : isDone
                          ? 'bg-slate-900/20'
                          : ''
                      }`}>
                        <div className="flex items-center gap-2">
                          <p className={`text-sm font-semibold ${
                            isActive ? 'text-white' : isDone ? 'text-slate-300' : 'text-slate-500'
                          }`}>
                            {step.label}
                          </p>
                          {isActive && (
                            <Loader2 size={14} className="text-blue-400 animate-spin" />
                          )}
                          {isDone && (
                            <span className="text-[10px] text-green-400 font-medium bg-green-500/10 px-1.5 py-0.5 rounded">Done</span>
                          )}
                        </div>
                        <p className={`text-xs mt-1 ${isActive ? 'text-slate-400' : 'text-slate-600'}`}>
                          {isActive ? STEP_TIPS[i] : step.description}
                        </p>

                        {isActive && (
                          <div className="mt-3 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full transition-all duration-1000 ease-out"
                              style={{
                                width: `${Math.min(
                                  ((progress - (i * 20)) / 20) * 100,
                                  95
                                )}%`,
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Overall progress */}
          {!failed && (
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-slate-900/60 border border-slate-700/40 rounded-full">
                <div className="w-32 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full transition-all duration-700 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <span className="text-xs text-slate-400 font-medium w-8">{progress}%</span>
              </div>
            </div>
          )}

          {/* Failure State */}
          {failed && failureInfo && (
            <div className="p-6 bg-red-900/15 border border-red-500/20 rounded-2xl mb-8">
              <p className="text-red-300 font-semibold mb-1">{failureInfo.title}</p>
              <p className="text-red-400/70 text-sm mb-5">{failureInfo.description}</p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => { window.location.href = FUNNEL_BASE_PATH; }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-xl text-sm font-medium text-white transition-colors"
                >
                  <RotateCcw size={14} />
                  Try again
                </button>
                <a
                  href="https://imagemotion.rehub.studio"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-xl text-sm font-medium text-white transition-colors"
                >
                  <Upload size={14} />
                  Full ImageMotion
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default ListingProcessing;
